import {
    getOwnedNodes,
    IMaterialData,
    keyForMeshMaterialData,
    Parameters,
    SceneNodes,
    SceneProperties,
    TemplateGraph,
    LodTypeValue,
    SceneNodePart,
} from "@cm/template-nodes"
import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_SCENE} from "@common/services/logging"
import {compressMeshGLTF} from "@app/template-editor/helpers/mesh-processing"
import {ThreeMesh} from "@app/template-editor/helpers/three-mesh"
import {getDefaultMaterial} from "@app/template-editor/services/three-material-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {GLTFBuilder} from "@cm/job-nodes/gltf-builder"
import {asFloat, DataResolver, getMappingMatrix, Mapping, MaterialConnection, TextureFromSet, TextureSet, UvMap} from "@cm/material-nodes"
import {firstValueFrom} from "rxjs"
import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {DataResolverService} from "@app/material-editor/services/data-resolver.service"

const DEFAULT_GEOMETRY_COMPRESSION_BIT_DEPTH = 14

@Injectable({
    providedIn: "root",
})
export class GltfExportService {
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)
    private dataResolver = inject(DataResolverService)

    async exportGltfFile(
        templateGraph: TemplateGraph,
        parameters: Parameters,
        sceneOptions:
            | {
                  maxSubdivisionLevel: number
                  maxSubdivisionLevelOnMobile?: number
              }
            | undefined,
        threeSceneManagerService: ThreeSceneManagerService,
        lodType: LodTypeValue,
        exportOptions?: {alphaBlendAsOpaque?: boolean},
    ): Promise<Uint8Array> {
        const clonedTemplateGraph = templateGraph.clone({cloneSubNode: () => true})

        const sceneProperties = new SceneProperties({
            parameters: {
                maxSubdivisionLevel: sceneOptions?.maxSubdivisionLevel ?? 9999,
                maxSubdivisionLevelOnMobile: sceneOptions?.maxSubdivisionLevelOnMobile ?? sceneOptions?.maxSubdivisionLevel ?? 9999,
                uiColor: [0, 0, 0],
                shiftAndZoomBackgroundImage: false,
                uiStyle: "default",
                iconSize: 24,
                enableAr: false,
                enableSalesEnquiry: false,
                textureResolution: "2000px",
                textureFiltering: false,
                textureStreamingMode: "off",
                enableRealtimeShadows: false,
                enableRealtimeLights: false,
                enableRealtimeMaterials: true,
                enableOnboardingHint: false,
                enableGltfDownload: false,
                enableStlDownload: false,
                enableSnapshot: false,
                enablePdfDownload: false,
                enableFullscreen: false,
                environmentMapMode: "full",
                showAnnotations: false,
                enableDimensionGuides: false,
                enableAdaptiveSubdivision: false,
                verticalArPlacement: false,
            },
        })
        clonedTemplateGraph.parameters.nodes.replaceParameters({list: [sceneProperties, ...getOwnedNodes(clonedTemplateGraph)]})

        const {sceneManagerService} = threeSceneManagerService

        sceneManagerService.$templateGraph.set(clonedTemplateGraph)
        sceneManagerService.$instanceParameters.set(parameters)
        sceneManagerService.$lodType.set(lodType)
        threeSceneManagerService.materialManagerService.$progressiveTextureLoading.set(false)

        sceneManagerService.compileTemplate()
        await threeSceneManagerService.sync(true)

        const meshes = sceneManagerService
            .$scene()
            .filter((x): x is SceneNodes.Mesh => SceneNodes.Mesh.is(x) && (!x.isProcedural || x.isDecal))
            .map<SceneNodePart>((x) => ({
                sceneNode: x,
                part: "root",
            }))

        const threeMeshes = threeSceneManagerService
            .getObjectsFromSceneNodes(meshes)
            .map((x) => x.threeObject)
            .filter((x): x is ThreeMesh => x instanceof ThreeMesh)

        const builder = new GLTFBuilder({
            generator: "colormass-exportScene",
        })
        builder.useExtension("KHR_draco_mesh_compression")

        const gltfScene = builder.addScene({
            nodes: [],
        })

        const materials = new Map<string, [number, THREE.Matrix4]>()
        for (const threeMesh of threeMeshes) {
            const parameters = threeMesh.getSceneNode()
            const {materialMap} = parameters

            for (const [materialIndex, materialData] of materialMap) {
                const mesh = threeMesh.getSubMesh(materialIndex)
                const material = mesh.material
                if (materialData) {
                    const key = keyForMeshMaterialData(materialData, parameters.meshRenderSettings)
                    if (materials.has(key)) continue

                    const mappingMatrix = getUVMappingMatrix(materialData, this.dataResolver.resolve)
                    const mappingMatrixInverse = mappingMatrix.clone().invert()

                    const uvTopLeft = new THREE.Vector4(0, 0, 0, 1).applyMatrix4(mappingMatrixInverse)
                    const uvTopRight = new THREE.Vector4(1, 0, 0, 1).applyMatrix4(mappingMatrixInverse)
                    const uvBottomRight = new THREE.Vector4(1, 1, 0, 1).applyMatrix4(mappingMatrixInverse)
                    const uvBottomLeft = new THREE.Vector4(0, 1, 0, 1).applyMatrix4(mappingMatrixInverse)

                    const {diffuseMap, diffuseAlphaMode, normalMap, metallicRoughnessMap, emissionMap} = await renderPbrMaps(
                        threeSceneManagerService,
                        material,
                        new THREE.Vector2(uvTopLeft.x, uvTopLeft.y),
                        new THREE.Vector2(uvTopRight.x, uvTopRight.y),
                        new THREE.Vector2(uvBottomRight.x, uvBottomRight.y),
                        new THREE.Vector2(uvBottomLeft.x, uvBottomLeft.y),
                        1024,
                        1024,
                        exportOptions?.alphaBlendAsOpaque ?? false,
                    )

                    const gltfMaterial = builder.addMaterial({
                        doubleSided: materialData.side === "double",
                    })

                    const diffuseTexture = await makeTexture(builder, diffuseMap)
                    const normalTexture = await makeTexture(builder, normalMap)
                    const metallicRoughnessTexture = await makeTexture(builder, metallicRoughnessMap)
                    gltfMaterial.data.pbrMetallicRoughness = {
                        baseColorTexture: {index: diffuseTexture.id},
                        metallicRoughnessTexture: {index: metallicRoughnessTexture.id},
                    }
                    gltfMaterial.data.normalTexture = {index: normalTexture.id}

                    if (material.transparent || material.alphaHash) {
                        if (material.alphaTest > 0) {
                            gltfMaterial.data.alphaMode = "MASK"
                            gltfMaterial.data.alphaCutoff = material.alphaTest
                        } else if (diffuseAlphaMode === "MASK") {
                            gltfMaterial.data.alphaMode = "MASK"
                            gltfMaterial.data.alphaCutoff = 0.5
                        } else if (diffuseAlphaMode === "BLEND") {
                            gltfMaterial.data.alphaMode = "BLEND"
                        }
                    }

                    if (emissionMap) {
                        const emissiveTexture = await makeTexture(builder, emissionMap)
                        gltfMaterial.data.emissiveTexture = {index: emissiveTexture.id}
                        gltfMaterial.data.emissiveFactor = [1, 1, 1]
                    }

                    materials.set(key, [gltfMaterial.id, mappingMatrix])
                } else {
                    const key = `default-${materialIndex}`

                    if (materials.has(key)) continue

                    const material = getDefaultMaterial(materialIndex)
                    const {color, roughness, metalness} = material

                    const gltfMaterial = builder.addMaterial({})

                    gltfMaterial.data.pbrMetallicRoughness = {
                        baseColorFactor: [color.r, color.g, color.b, 1],
                        metallicFactor: metalness,
                        roughnessFactor: roughness,
                    }

                    materials.set(key, [gltfMaterial.id, new THREE.Matrix4().identity()])
                }
            }
        }

        for (const threeMesh of threeMeshes) {
            const parameters = threeMesh.getSceneNode()
            const {materialMap} = parameters

            for (const [materialIndex, materialData] of materialMap) {
                const mesh = threeMesh.getSubMesh(materialIndex)

                const gltfMaterialData = (() => {
                    if (!materialData) return materials.get(`default-${materialIndex}`)

                    const key = keyForMeshMaterialData(materialData, parameters.meshRenderSettings)
                    return materials.get(key)
                })()
                const gltfMaterialID = gltfMaterialData?.[0]
                const gltfMaterialMatrix = gltfMaterialData?.[1]

                const positionArray = mesh.geometry.attributes.position.array
                if (!(positionArray instanceof Float32Array)) throw new Error("Position array is not a Float32Array")

                const normalArrayRaw = mesh.geometry.attributes.normal.array
                if (!(normalArrayRaw instanceof Float32Array)) throw new Error("Normal array is not a Float32Array")
                const normalArray = fixNormals(normalArrayRaw, materialData?.side === "back")

                const uvArrayRaw = mesh.geometry.attributes.uv.array
                if (!(uvArrayRaw instanceof Float32Array)) throw new Error("UV array is not a Float32Array")
                const uvArray = gltfMaterialMatrix ? transformUVs(uvArrayRaw, gltfMaterialMatrix) : uvArrayRaw

                const index = mesh.geometry.index
                if (!index) throw new Error("Mesh geometry has no index")
                const indices = new Uint32Array(index.array)

                const geometryCompressionBitDepth = DEFAULT_GEOMETRY_COMPRESSION_BIT_DEPTH

                const dracoFile = await firstValueFrom(
                    compressMeshGLTF(sceneManagerService.workerService, positionArray, normalArray, uvArray, indices, geometryCompressionBitDepth),
                )

                const gltfDracoBuf = builder.addBuffer(
                    {
                        byteLength: dracoFile!.byteLength,
                    },
                    dracoFile!,
                )
                const gltfDracoView = builder.addBufferView({
                    buffer: gltfDracoBuf.id,
                    byteLength: dracoFile!.byteLength,
                })
                const gltfMesh = builder.addMesh({
                    primitives: [
                        {
                            attributes: {
                                POSITION: makeAccessor(builder, positionArray, 3).id,
                                NORMAL: makeAccessor(builder, normalArray, 3).id,
                                TEXCOORD_0: gltfMaterialID !== undefined ? makeAccessor(builder, uvArray, 2).id : undefined,
                            },
                            indices: makeIndicesAccessor(builder, indices.length).id,
                            material: gltfMaterialID,
                            extensions: {
                                KHR_draco_mesh_compression: {
                                    bufferView: gltfDracoView.id,
                                    attributes: {
                                        POSITION: 0, //TODO: get these attribute IDs from dracoWriter.cpp!
                                        NORMAL: 1,
                                        TEXCOORD_0: gltfMaterialID !== undefined ? 2 : undefined,
                                    },
                                },
                            },
                        },
                    ],
                })
                const gltfNode = builder.addNode({
                    matrix: exportMatrix(threeMesh.getRenderObject().matrix),
                    mesh: gltfMesh.id,
                })
                gltfScene.data.nodes.push(gltfNode.id)

                this.logger.debug("Exported mesh", mesh)
            }
        }

        return builder.generateGLB()
    }
}

const getUVMappingMatrix = (materialData: IMaterialData, _resolve: DataResolver) => {
    const materialNodes = materialData.materialGraph.nodes

    const getLargestMappingNode = (mappingNodes: Mapping[]) => {
        const scaleArea = (mappingNode: Mapping) => {
            const mappingMatrix = getMappingMatrix(mappingNode.parameters.settings)
            const scale = new THREE.Vector3()
            mappingMatrix.decompose(new THREE.Vector3(), new THREE.Quaternion(), scale)
            scale.x = 1.0 / scale.x
            scale.y = 1.0 / scale.y
            scale.z = 1.0 / scale.z

            return scale.x * scale.y
        }

        return mappingNodes.length > 1 ? mappingNodes.reduce((prev, curr) => (scaleArea(prev) < scaleArea(curr) ? curr : prev)) : mappingNodes.at(0)
    }

    let mappingNode = getLargestMappingNode(
        (() => {
            const mappingNodeCandidates = new Set<Mapping>()

            const uvMaps = materialNodes.filter(
                (x): x is UvMap => x instanceof UvMap && (x.parameters.settings.uvMapIndex === undefined || x.parameters.settings.uvMapIndex === 0),
            )
            for (const uvMap of uvMaps) {
                for (const parent of uvMap.parents) {
                    if (parent instanceof MaterialConnection) {
                        const propertyParent = parent.destination
                        if (propertyParent instanceof Mapping) mappingNodeCandidates.add(propertyParent)
                    }
                }
            }

            const unConnectedMappingLikeNodes = materialNodes.filter((x): x is Mapping | TextureSet | TextureFromSet => {
                if (x instanceof Mapping) return x.parameters.settings.vector === undefined
                else if (x instanceof TextureSet || x instanceof TextureFromSet) {
                    return x.parameters.settings.uv === undefined
                } else return false
            })
            for (const mappingLikeNode of unConnectedMappingLikeNodes) {
                if (mappingLikeNode instanceof Mapping) mappingNodeCandidates.add(mappingLikeNode)
            }

            return [...mappingNodeCandidates]
        })(),
    )

    let mappingMatrix = new THREE.Matrix4().identity()
    while (mappingNode) {
        mappingMatrix = getMappingMatrix(mappingNode.parameters.settings).multiply(mappingMatrix)

        const nextMappingNodeCandidates = new Set<Mapping>()
        for (const parent of mappingNode.parents) {
            if (parent instanceof MaterialConnection) {
                const propertyParent = parent.destination
                if (propertyParent instanceof Mapping) nextMappingNodeCandidates.add(propertyParent)
            }
        }

        mappingNode = getLargestMappingNode([...nextMappingNodeCandidates])
    }

    return mappingMatrix
}

const fixNormals = (array: Float32Array, invert: boolean): Float32Array => {
    const elemSize = 3
    const count = array.length / elemSize
    const newArray = new Float32Array(array.length)

    for (let i = 0; i < count; i++) {
        const idx = i * elemSize
        let [x, y, z] = array.subarray(idx, idx + elemSize)

        const magnitudeSquared = x * x + y * y + z * z

        if (magnitudeSquared < 1e-3) {
            ;[x, y, z] = [0, 0, 1]
        } else if (magnitudeSquared < 0.9999) {
            const scale = 1 / Math.sqrt(magnitudeSquared)
            ;[x, y, z] = [x * scale, y * scale, z * scale]
        }

        if (invert) [x, y, z] = [-x, -y, -z]

        newArray.set([x, y, z], idx)
    }

    return newArray
}

const transformUVs = (array: Float32Array, matrix: THREE.Matrix4): Float32Array => {
    const elemSize = 2
    const count = array.length / elemSize
    const newArray = new Float32Array(array.length)

    for (let i = 0; i < count; i++) {
        const idx = i * elemSize
        const [u, v] = array.subarray(idx, idx + elemSize)

        const mappedUv = new THREE.Vector4(u, v, 0, 1).applyMatrix4(matrix)

        newArray.set([mappedUv.x, mappedUv.y], idx)
    }

    return newArray
}

const makeTexture = async (builder: GLTFBuilder, blob: Blob) => {
    const {type} = blob
    if (type !== "image/png" && type !== "image/jpeg") throw new Error(`Unsupported image type: ${type}`)
    const data = new Uint8Array(await blob.arrayBuffer())

    type GLTFTextureType = "image/png" | "image/jpeg"
    const mimeType = type as GLTFTextureType

    const gltfImageBuffer = builder.addBuffer(
        {
            byteLength: data.byteLength,
        },
        data,
    )
    const gltfImageBufferView = builder.addBufferView({
        buffer: gltfImageBuffer.id,
        byteLength: data.byteLength,
    })
    const gltfImage = builder.addImage({
        bufferView: gltfImageBufferView.id,
        mimeType,
    })
    // const gltfSampler = builder.addSampler({
    //     minFilter: GLTFBuilder.SamplerFilter.LINEAR_MIPMAP_LINEAR,
    //     magFilter: GLTFBuilder.SamplerFilter.LINEAR,
    //     wrapS: GLTFBuilder.SamplerWrap.REPEAT,
    //     wrapT: GLTFBuilder.SamplerWrap.REPEAT
    // });
    const gltfTexture = builder.addTexture({
        source: gltfImage.id,
        //sampler: gltfSampler.id,
    })

    return gltfTexture
}

const makeAccessor = (builder: GLTFBuilder, array: Float32Array, elemSize: number) => {
    const count = array.length / elemSize
    const rangeMin: number[] = []
    const rangeMax: number[] = []

    for (let elemOffset = 0; elemOffset < elemSize; elemOffset++) {
        let min = Infinity
        let max = -Infinity

        for (let i = 0; i < count; i++) {
            const val = array[i * elemSize + elemOffset]
            min = Math.min(min, val)
            max = Math.max(max, val)
        }

        rangeMin.push(min)
        rangeMax.push(max)
    }

    const getType = () => {
        switch (elemSize) {
            case 1:
                return "SCALAR"
            case 2:
                return "VEC2"
            case 3:
                return "VEC3"
            case 4:
                return "VEC4"
            default:
                throw new Error("Invalid element size")
        }
    }

    const accessor = builder.addAccessor({
        type: getType(),
        componentType: GLTFBuilder.ComponentType.FLOAT,
        count,
        min: rangeMin,
        max: rangeMax,
    })

    return accessor
}

const makeIndicesAccessor = (builder: GLTFBuilder, count: number) => {
    const accessor = builder.addAccessor({
        type: "SCALAR",
        componentType: GLTFBuilder.ComponentType.UNSIGNED_INT,
        count,
    })

    return accessor
}

const exportMatrix = (matrix: THREE.Matrix4) => {
    const identityMatrix = new THREE.Matrix4().identity()
    const scaleMatrix = new THREE.Matrix4().makeScale(0.01, 0.01, 0.01) // convert from centimeters to meters

    const scaledMatrix = scaleMatrix.multiply(matrix)
    // GLTF validation requires that the node matrix is omitted if it is equal to identity
    if (scaledMatrix.equals(identityMatrix)) {
        return undefined
    } else {
        return scaledMatrix.toArray()
    }
}

const createQuadGeometry = (
    positionTopLeft: THREE.Vector2,
    positionTopRight: THREE.Vector2,
    positionBottomRight: THREE.Vector2,
    positionBottomLeft: THREE.Vector2,
    uvTopLeft: THREE.Vector2,
    uvTopRight: THREE.Vector2,
    uvBottomRight: THREE.Vector2,
    uvBottomLeft: THREE.Vector2,
) => {
    const vertices = [
        positionTopLeft.x,
        positionTopLeft.y,
        0,
        positionTopRight.x,
        positionTopRight.y,
        0,
        positionBottomRight.x,
        positionBottomRight.y,
        0,
        positionBottomLeft.x,
        positionBottomLeft.y,
        0,
    ]
    const normals = [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1]
    const uvs = [uvTopLeft.x, uvTopLeft.y, uvTopRight.x, uvTopRight.y, uvBottomRight.x, uvBottomRight.y, uvBottomLeft.x, uvBottomLeft.y]
    const indices = [0, 1, 2, 2, 3, 0]

    const geometry = new THREE.BufferGeometry()
    geometry.setIndex(indices)
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3))
    geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3))
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2))

    return geometry
}

const OPAQUE_ALPHA_THRESHOLD = 250
const TRANSPARENT_ALPHA_THRESHOLD = 5
const TRANSMISSION_MAX_TRANSPARENCY = 0.75

const classifyAlpha = (rgbaData: ArrayLike<number>): "OPAQUE" | "MASK" | "BLEND" => {
    let opaque = true
    for (let i = 3; i < rgbaData.length; i += 4) {
        const alpha = rgbaData[i]
        if (alpha >= OPAQUE_ALPHA_THRESHOLD) continue
        if (alpha > TRANSPARENT_ALPHA_THRESHOLD) return "BLEND"
        opaque = false
    }
    return opaque ? "OPAQUE" : "MASK"
}

const renderPbrMaps = async (
    threeSceneManagerService: ThreeSceneManagerService,
    material: THREE.Material,
    uvTopLeft: THREE.Vector2,
    uvTopRight: THREE.Vector2,
    uvBottomRight: THREE.Vector2,
    uvBottomLeft: THREE.Vector2,
    width: number,
    height: number,
    alphaBlendAsOpaque: boolean,
) => {
    const renderer = await threeSceneManagerService.getRenderer()

    const webGPUMode = threeSceneManagerService.isWebGPUMode()

    if (webGPUMode && width % 16 !== 0) throw new Error("Width must be multiple of 16 pixels in WebGPU mode")

    const renderScene = new THREE.Scene()

    const patchedMaterial = material.clone()
    patchedMaterial.alphaTest = 0
    patchedMaterial.alphaHash = false
    patchedMaterial.transparent = true
    patchedMaterial.blending = THREE.NoBlending

    if (!(patchedMaterial instanceof THREE.MeshStandardNodeMaterial)) throw new Error("Material is not a MeshStandardNodeMaterial")

    //Transmission is approximated as alpha-blended
    if (patchedMaterial instanceof THREE.MeshPhysicalNodeMaterial && patchedMaterial.transmissionNode) {
        patchedMaterial.opacityNode = TSL.sub(1.0, TSL.mul(TSL.clamp(asFloat(patchedMaterial.transmissionNode), 0.0, 1.0), TRANSMISSION_MAX_TRANSPARENCY))
        patchedMaterial.transmissionNode = null
        patchedMaterial.transmission = 0
    }

    const outputType = TSL.uniform(0)
    const normalYFlip = TSL.uniform(webGPUMode ? -1.0 : 1.0)

    const resolve = TSL.Fn(() => {
        const result = TSL.vec4(1.0).toVar()

        TSL.If(outputType.equal(0), () => {
            result.assign(TSL.vec4(TSL.diffuseColor))
        })
            .ElseIf(outputType.equal(1), () => {
                // Flip normal Y in WebGPU mode to compensate for UV flip
                const normal = TSL.vec3(TSL.normalView.x, TSL.normalView.y.mul(normalYFlip), TSL.normalView.z)
                result.assign(TSL.vec4(normal.mul(0.5).add(0.5), 1.0))
            })
            .ElseIf(outputType.equal(2), () => {
                result.assign(TSL.vec4(0.0, TSL.roughness, TSL.metalness, 1.0))
            })
            .ElseIf(outputType.equal(3), () => {
                result.assign(TSL.vec4(TSL.emissive, 1.0))
            })

        return result
    })

    patchedMaterial.outputNode = resolve()

    // In WebGPU mode, swap top/bottom UVs to flip the rendered image
    const planeMesh = new THREE.Mesh(
        webGPUMode
            ? createQuadGeometry(
                  new THREE.Vector2(-0.5, -0.5),
                  new THREE.Vector2(0.5, -0.5),
                  new THREE.Vector2(0.5, 0.5),
                  new THREE.Vector2(-0.5, 0.5),
                  uvBottomLeft,
                  uvBottomRight,
                  uvTopRight,
                  uvTopLeft,
              )
            : createQuadGeometry(
                  new THREE.Vector2(-0.5, -0.5),
                  new THREE.Vector2(0.5, -0.5),
                  new THREE.Vector2(0.5, 0.5),
                  new THREE.Vector2(-0.5, 0.5),
                  uvTopLeft,
                  uvTopRight,
                  uvBottomRight,
                  uvBottomLeft,
              ),
        patchedMaterial,
    )
    renderScene.add(planeMesh)

    const renderCamera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 2)
    renderCamera.position.z = 1

    await renderer.init()

    type OutputMapType = "diffuse" | "normal" | "metallicRoughness" | "emission"

    const renderMap = async (outputMapName: OutputMapType) => {
        switch (outputMapName) {
            case "diffuse":
                outputType.value = 0
                break
            case "normal":
                outputType.value = 1
                break
            case "metallicRoughness":
                outputType.value = 2
                break
            case "emission":
                outputType.value = 3
                break
        }

        const renderTarget = new THREE.RenderTarget(width, height, {
            generateMipmaps: false,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RGBAFormat,
            colorSpace: outputMapName === "diffuse" ? THREE.SRGBColorSpace : THREE.NoColorSpace,
        })

        const rendererState = THREE.RendererUtils.saveRendererState(renderer)

        renderer.setRenderTarget(renderTarget)

        renderer.clear()
        renderer.render(renderScene, renderCamera)

        THREE.RendererUtils.restoreRendererState(renderer, rendererState)

        const dataArray = await renderer.readRenderTargetPixelsAsync(renderTarget, 0, 0, width, height)
        renderTarget.dispose()

        let alphaMode = outputMapName === "diffuse" && (material.transparent || material.alphaHash) ? classifyAlpha(dataArray) : undefined
        if (alphaMode === "BLEND" && alphaBlendAsOpaque) alphaMode = "OPAQUE"

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext("2d")
        if (!ctx) throw new Error("Failed to get 2d context from canvas")

        const imageData = new ImageData(new Uint8ClampedArray(dataArray), width, height)
        ctx.putImageData(imageData, 0, 0)

        const blob = await new Promise<Blob>((resolve, reject) =>
            canvas.toBlob(
                function (blob) {
                    if (!blob) reject("Failed to create blob")
                    else resolve(blob)
                },
                alphaMode === "MASK" || alphaMode === "BLEND" ? "image/png" : "image/jpeg",
            ),
        )

        return {blob, alphaMode}
    }

    const [diffuse, normal, metallicRoughness, emission] = await Promise.all([
        renderMap("diffuse"),
        renderMap("normal"),
        renderMap("metallicRoughness"),
        (() => {
            const isColorSet = (color: THREE.Color) => color.r !== 0 || color.g !== 0 || color.b !== 0
            if (
                (material instanceof THREE.MeshStandardNodeMaterial && material.emissiveNode) ||
                (material instanceof THREE.MeshStandardMaterial && isColorSet(material.emissive))
            )
                return renderMap("emission")
            else return undefined
        })(),
    ])

    planeMesh.geometry.dispose()
    planeMesh.material.dispose()

    return {
        diffuseMap: diffuse.blob,
        diffuseAlphaMode: diffuse.alphaMode,
        normalMap: normal.blob,
        metallicRoughnessMap: metallicRoughness.blob,
        emissionMap: emission?.blob,
    }
}
